
import { User, SystemEvent, NotificationSettings } from '../types';

export const notificationService = {
  /**
   * Handshakes with the browser to authorize push shards.
   */
  async requestPermission(): Promise<boolean> {
    if (!('Notification' in window)) return false;
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  },

  /**
   * Verifies if the system is within Operation Quiet Hours.
   */
  isQuietHours(settings?: NotificationSettings): boolean {
    if (!settings || !settings.quietHoursEnabled) return false;

    const now = new Date();
    const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
    
    const start = settings.quietHoursStart;
    const end = settings.quietHoursEnd;

    if (start < end) {
      return currentTime >= start && currentTime <= end;
    } else {
      // Handles overnight ranges (e.g., 22:00 to 08:00)
      return currentTime >= start || currentTime <= end;
    }
  },

  /**
   * Dispatches a concave mobile alert if operational conditions are met.
   */
  async dispatchAlert(user: User, event: SystemEvent, title: string, body: string) {
    const settings = user.settings?.notifications;
    
    // FORCED DEFAULT TO TRUE: Mobile Push Alerts are now ON by default for all users.
    // They are only disabled if the user has explicitly set the flag to false.
    const isPushEnabled = settings?.pushEnabled !== false;
    
    if (!isPushEnabled) return;
    
    // Check if the specific event is in the notification registry
    // Default to including nudges and critical updates if the list is missing
    const allowedEvents = settings?.notifyOn || ['nudge_urgent', 'nudge_strategic', 'completed', 'partner_request', 'partner_update', 'daily'];
    if (!allowedEvents.includes(event)) return;
    
    if (this.isQuietHours(settings)) {
      console.log("[Vanguard] Operational Quiet Hours active. Alert suppressed.");
      return;
    }

    if (Notification.permission === 'granted' && 'serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.ready;
        // Fix: Added type assertion to any for the options object to bypass strict NotificationOptions check 
        // for the 'renotify' property which is supported by most modern browsers but sometimes missing from TS lib definitions.
        registration.showNotification(title, {
          body,
          icon: '/favicon.ico',
          badge: '/favicon.ico',
          tag: 'vanguard-alert',
          renotify: true,
          data: { url: window.location.origin }
        } as any);
      } catch (err) {
        console.warn("[Vanguard] Push dispatch failed. Reverting to fallback.", err);
      }
    } else if (Notification.permission === 'default' && isPushEnabled) {
      // If we haven't asked yet but the user hasn't said no, try local send as a hint
      this.sendLocal(title, body);
    }
  },

  /**
   * Fallback for immediate visual feedback if the app is focused.
   */
  sendLocal(title: string, body: string) {
    if (Notification.permission === 'granted') {
      new Notification(title, { body });
    }
  }
};
